import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';

// React-facing wrapper around the service worker lifecycle, aligned
// with manuals/pwa-install-update-spec.md v2.0.
//
// Two builds are tracked:
//   • clientBuild — version bundled into THIS tab's JS (immutable
//     until the next page load). Source: __APP_VERSION__ Vite define.
//   • serverBuild — what GET /api/version reports right now. Polled
//     every 15 s. After a fresh deploy this flips first, before the
//     browser has noticed the new sw.js bytes.
//
// `updateAvailable` lights up when EITHER:
//   • the SW has a `waiting` worker (clean path), OR
//   • clientBuild !== serverBuild (build-mismatch fallback)
//
// The fallback saves us when reg.update() is delayed by HTTP
// heuristics or the browser is being weird about sw.js byte-diffs.
//
// applyUpdate() runs the three-step strategy from spec §0.3:
//   1. reg.waiting? → SKIP_WAITING → controllerchange → reload
//   2. else reg.update() and re-check waiting
//   3. else build-mismatch → controlled location.reload()
//
// All reloads route through updateRequestedRef so a stray
// controllerchange (browsers fire it on their own in some edge cases)
// never reloads the page silently.

const SW_URL              = '/sw.js';
const VERSION_POLL_MS     = 15_000;
const SW_UPDATE_POLL_MS   = 10 * 60 * 1000;  // safety net for idle tabs
const PHANTOM_GUARD_KEY   = 'pwa-phantom-recovery';

const PwaContext = createContext(null);

function detectIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const iPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(platform) || /iPad|iPhone|iPod/.test(ua) || iPadOs;
}

function detectSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

const CLIENT_BUILD = (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev').trim();

export function PwaProvider({ children }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(() =>
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
     window.navigator?.standalone === true)
  );
  const [updateReady, setUpdateReady] = useState(false);
  const [serverBuild, setServerBuild] = useState(null);
  const [isIos]    = useState(() => detectIos());
  const [isSafari] = useState(() => detectSafari());

  // Refs — non-reactive plumbing. Spec §0.4: keeping these out of
  // state avoids re-render churn and stale closures inside the SW
  // lifecycle effect (which runs once per mount, see below).
  const registrationRef    = useRef(null);
  const waitingRef         = useRef(null);
  const updateRequestedRef = useRef(false);  // set true ONLY by user action
  // serverBuildRef mirrors the serverBuild state so the SW useEffect
  // can read the latest value without a stale closure or a re-mount.
  const serverBuildRef     = useRef(null);

  // Push updateReady from current registration + serverBuildRef. Uses
  // refs for inputs so the function identity stays stable — callers
  // don't need to be re-wired when serverBuild changes.
  const syncUpdateReady = useCallback(() => {
    const reg = registrationRef.current;
    const hasWaiting = !!(reg && reg.waiting);
    if (hasWaiting) waitingRef.current = reg.waiting;
    const sb = serverBuildRef.current;
    const mismatch = !!sb && CLIENT_BUILD !== 'dev' && sb !== CLIENT_BUILD;
    setUpdateReady(hasWaiting || mismatch);
  }, []);

  // ── beforeinstallprompt + appinstalled ───────────────────────────
  useEffect(() => {
    function onBIP(e) {
      e.preventDefault();
      setInstallPrompt(e);
    }
    function onInstalled() {
      setInstalled(true);
      setInstallPrompt(null);
    }
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // ── 15-second /api/version polling ───────────────────────────────
  // Spec §5: 15 s is the primary mechanism for operational UI. As
  // soon as the server reports a new build we (a) update local state
  // so syncUpdateReady can flip updateAvailable, and (b) proactively
  // probe the SW so the clean SKIP_WAITING branch wins on click.
  useEffect(() => {
    let disposed = false;
    let timer = null;
    async function tick() {
      try {
        const res = await fetch('/api/version', {
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!res.ok) return;
        const data = await res.json();
        if (disposed) return;
        const v = String(data?.version ?? '').trim();
        if (!v || serverBuildRef.current === v) return;
        serverBuildRef.current = v;
        setServerBuild(v);
        if (registrationRef.current) {
          registrationRef.current.update().catch(() => {});
        }
        syncUpdateReady();
      } catch { /* network blip */ }
    }
    tick();
    timer = setInterval(tick, VERSION_POLL_MS);
    return () => { disposed = true; if (timer) clearInterval(timer); };
  }, [syncUpdateReady]);

  // ── SW register + lifecycle ──────────────────────────────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (import.meta.env.DEV) return;

    let disposed = false;
    let pollTimer = null;

    function pollSwUpdate() {
      const reg = registrationRef.current;
      if (reg) reg.update().catch(() => {});
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') pollSwUpdate();
    }
    function onFocus() { pollSwUpdate(); }

    (async () => {
      // PHANTOM CONTROLLER RECOVERY — if controller is set but the
      // registry is empty (DevTools "Unregister" without page reload,
      // or a registry edge case), the next register() creates a
      // brand-new registration that goes straight to active without
      // passing through installing/waiting. updatefound never fires
      // and Update PWA can never light up. One-shot reload fixes it;
      // the sessionStorage flag guards against a loop.
      if (navigator.serviceWorker.controller) {
        const existing = await navigator.serviceWorker.getRegistrations().catch(() => []);
        if (existing.length === 0 && !sessionStorage.getItem(PHANTOM_GUARD_KEY)) {
          sessionStorage.setItem(PHANTOM_GUARD_KEY, '1');
          window.location.reload();
          return;
        }
      }
      sessionStorage.removeItem(PHANTOM_GUARD_KEY);

      const reg = await navigator.serviceWorker
        .register(SW_URL, { scope: '/' })
        .catch(() => null);
      if (disposed || !reg) return;
      registrationRef.current = reg;

      // Worker may already be waiting at registration time (previous
      // tab closed before applying the update).
      if (reg.waiting && navigator.serviceWorker.controller) {
        waitingRef.current = reg.waiting;
        syncUpdateReady();
      }

      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            waitingRef.current = nw;
            syncUpdateReady();
          }
        });
      });

      // 10-minute safety-net poll. /api/version polling is the primary
      // operational mechanism; this catches the case where the API is
      // down but a new sw.js has been deployed anyway.
      pollTimer = setInterval(pollSwUpdate, SW_UPDATE_POLL_MS);
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', onFocus);
    })();

    // Reload ONLY on explicit user action — spec §0.2. A stray
    // controllerchange (e.g. SW activation racing with the page) must
    // never reload the page silently. updateRequestedRef is the
    // single source of truth here, cleared right after consuming so
    // the same flag can't trigger two reloads.
    function onControllerChange() {
      if (!updateRequestedRef.current) return;
      updateRequestedRef.current = false;
      window.location.reload();
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      disposed = true;
      if (pollTimer) clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, [syncUpdateReady]);

  async function promptInstall() {
    if (!installPrompt) return;
    try {
      installPrompt.prompt();
      await installPrompt.userChoice;
    } catch { /* user-cancelled */ }
    setInstallPrompt(null);
  }

  // Three-step Update strategy (spec §0.3).
  const applyUpdate = useCallback(async () => {
    const reg = registrationRef.current;
    const sb = serverBuildRef.current;
    const hasNewerServer = !!sb && CLIENT_BUILD !== 'dev' && sb !== CLIENT_BUILD;

    // Step 0: no registration → only reload-fallback is possible.
    if (!reg) {
      if (hasNewerServer) {
        updateRequestedRef.current = true;
        window.location.reload();
      }
      return;
    }

    // Step 1: waiting worker already there → just activate it.
    if (reg.waiting) {
      updateRequestedRef.current = true;
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }

    // Step 2: maybe a probe finds a new sw.js right now.
    try { await reg.update(); } catch { /* network blip */ }
    if (reg.waiting) {
      updateRequestedRef.current = true;
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }

    // Step 3: SW didn't see a new worker but the server build differs
    // from ours — controlled reload picks up the new bundle via
    // nginx's no-cache index.html.
    if (hasNewerServer) {
      updateRequestedRef.current = true;
      window.location.reload();
    }
  }, []);

  const value = useMemo(() => ({
    installable: !!installPrompt && !installed,
    iosInstallAvailable: isIos && isSafari && !installed,
    installed,
    updateAvailable: updateReady,
    clientBuild: CLIENT_BUILD,
    serverBuild,
    promptInstall,
    applyUpdate
  }), [installPrompt, installed, isIos, isSafari, updateReady, serverBuild, applyUpdate]);

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}

export function usePwa() {
  const c = useContext(PwaContext);
  if (!c) throw new Error('usePwa must be used within PwaProvider');
  return c;
}
